import { createContext, ReactNode, useContext, useState } from 'react';

import { MatchSide, RetireOption, RetirementScores, AdjustedScores } from '@/components/constants/types';
import { SelectedPlayers } from '@/components/constants/types';
import { Matchup, Match, TeamLeaguePlayerInfo } from '@/entities';
import { GameCompletedEnum } from '@/entities/matchup/constants';
import { MatchScores } from '@/lib/types';
import { calculateRetirementScores, gameNumbers } from '@/utils/helpers';

interface LineupState {
	matchId: string;
	isSavedLineup: boolean;
	selectedPlayers: SelectedPlayers;
}

interface ScoresState {
	matchId: string;
	scores: MatchScores;
}

const initialSelectedPlayers: SelectedPlayers = {
	teamOnePlayerOneUuid: null,
	teamOnePlayerTwoUuid: null,
	teamTwoPlayerOneUuid: null,
	teamTwoPlayerTwoUuid: null
};

const initialScores: MatchScores = {
	home: {
		game1: '0',
		gameOneStatus: '',
		game2: '0',
		gameTwoStatus: '',
		game3: '0',
		gameThreeStatus: '',
		game4: '0',
		gameFourStatus: '',
		game5: '0',
		gameFiveStatus: '',
		final: '0'
	},
	away: {
		game1: '0',
		gameOneStatus: '',
		game2: '0',
		gameTwoStatus: '',
		game3: '0',
		gameThreeStatus: '',
		game4: '0',
		gameFourStatus: '',
		game5: '0',
		gameFiveStatus: '',
		final: '0'
	}
};

interface ScoreCardContextType {
	lineups: LineupState[];
	updateMatchLineupStatus: (matchId: string, isSaved: boolean) => void;
	getMatchLineupStatus: (matchId: string) => boolean;
	areAllLineupsSaved: boolean;
	updateSelectedPlayers: (matchId: string, playerType: string, playerId: string) => void;
	clearSelectedPlayers: (matchId: string) => void;
	getSelectedPlayers: (matchId: string) => SelectedPlayers;
	haveSelectedPlayersChanged: (matchId: string) => boolean;
	updateMatchScores: (matchId: string, team: MatchSide, scores: Record<string, string>) => void;
	getMatchScores: (matchId: string) => MatchScores;
	updateMatchFinalScores: (matchId: string, finalScores: { teamOneFinalScore: number; teamTwoFinalScore: number }) => void;
	matchupData: Matchup;
	tempMatchRetireScores: { homeRetiredGameValue: string | undefined; awayRetiredGameValue: string | undefined };
	setTempMatchRetireScores: (value: { homeRetiredGameValue: string | undefined; awayRetiredGameValue: string | undefined }) => void;
	saveRetireScores: (matchId: string, gameNumber: string, team: MatchSide, retireStatus: RetireOption) => void;
	getRetiredGameNumber: (matchId: string) => string[];
	getRetiredTeam: (matchId: string) => Record<string, MatchSide>;
	getRetiredOption: (matchId: string) => Record<string, RetireOption>;
	searchTermPlayer: string;
	setSearchTermPlayer: (term: string) => void;
	checkAndSetRetiredStatus: (match: Match) => void;
	getMatchWinner: (matchId: string) => 'home' | 'away' | 'draw';
	getAdjustedScores: (matchId: string, gameNumber: string, games: { max: number; winBy: number }[]) => RetirementScores;
	getAllAdjustedScores: (matchId: string, games: { max: number; winBy: number }[]) => AdjustedScores;
	getPlayerDoublesRating: (players: TeamLeaguePlayerInfo[], playerUuid: string) => number;
}

interface ScoreCardProviderProps {
	children: ReactNode;
	initialMatches: string[];
	matchupData: Matchup;
}

const ScoreCardContext = createContext<ScoreCardContextType | undefined>(undefined);

export const ScoreCardProvider = ({ children, initialMatches, matchupData }: ScoreCardProviderProps) => {
	const [lineups, setLineups] = useState<LineupState[]>(
		initialMatches.map((matchId) => ({
			matchId,
			isSavedLineup: true,
			selectedPlayers: initialSelectedPlayers
		}))
	);

	const [matchScores, setMatchScores] = useState<ScoresState[]>(
		initialMatches.map((matchId) => ({
			matchId,
			scores: initialScores
		}))
	);

	const [tempMatchRetireScores, setTempMatchRetireScores] = useState<{
		homeRetiredGameValue: string | undefined;
		awayRetiredGameValue: string | undefined;
	}>({
		homeRetiredGameValue: undefined,
		awayRetiredGameValue: undefined
	});

	const [searchTermPlayer, setSearchTermPlayer] = useState<string>('');

	const updateMatchLineupStatus = (matchId: string, isSaved: boolean) => {
		setLineups((prev) => prev.map((match) => (match.matchId === matchId ? { ...match, isSavedLineup: isSaved } : match)));
	};

	const getMatchLineupStatus = (matchId: string): boolean => {
		return lineups.find((match) => match.matchId === matchId)?.isSavedLineup ?? true;
	};

	const getPlayerDoublesRating = (players: TeamLeaguePlayerInfo[], playerUuid: string) => {
		const player = players.find((player) => player.uuid === playerUuid);
		return player?.ratingDoubles || 0;
	};

	const areAllLineupsSaved = lineups.every((match) => match.isSavedLineup);

	const updateSelectedPlayers = (matchId: string, playerType: string, playerId: string) => {
		setLineups((prev) =>
			prev.map((match) =>
				match.matchId === matchId
					? {
							...match,
							selectedPlayers: {
								...match.selectedPlayers,
								[playerType]: playerId
							}
						}
					: match
			)
		);
	};

	const clearSelectedPlayers = (matchId: string) => {
		setLineups((prev) => prev.map((match) => (match.matchId === matchId ? { ...match, selectedPlayers: initialSelectedPlayers } : match)));
	};

	const getSelectedPlayers = (matchId: string): SelectedPlayers => {
		return lineups.find((match) => match.matchId === matchId)?.selectedPlayers || initialSelectedPlayers;
	};

	const haveSelectedPlayersChanged = (matchId: string): boolean => {
		const currentPlayers = getSelectedPlayers(matchId);
		return (
			currentPlayers.teamOnePlayerOneUuid !== initialSelectedPlayers.teamOnePlayerOneUuid ||
			currentPlayers.teamOnePlayerTwoUuid !== initialSelectedPlayers.teamOnePlayerTwoUuid ||
			currentPlayers.teamTwoPlayerOneUuid !== initialSelectedPlayers.teamTwoPlayerOneUuid ||
			currentPlayers.teamTwoPlayerTwoUuid !== initialSelectedPlayers.teamTwoPlayerTwoUuid
		);
	};

	const updateMatchScores = (matchId: string, team: MatchSide, newScores: Record<string, string>) => {
		setMatchScores((prev) =>
			prev.map((match) =>
				match.matchId === matchId
					? {
							...match,
							scores: {
								...match.scores,
								[team]: {
									...match.scores[team],
									...newScores
								}
							}
						}
					: match
			)
		);
	};

	const getMatchScores = (matchId: string): MatchScores => {
		return matchScores.find((match) => match.matchId === matchId)?.scores || initialScores;
	};

	const checkAndSetRetiredStatus = (match: Match) => {
		// Find the first game with a retirement/forfeit status
		for (const gameWord of gameNumbers) {
			const statusField = `game${gameWord}Status` as keyof typeof match;
			const statusValue = match[statusField];

			// Check if we have a valid status using the enum
			if (
				statusValue === GameCompletedEnum.FORFEIT_TEAM_ONE_GAME_COMPLETED_TYPE.toString() ||
				statusValue === GameCompletedEnum.RETIREMENT_TEAM_ONE_GAME_COMPLETED_TYPE.toString() ||
				statusValue === GameCompletedEnum.FORFEIT_TEAM_TWO_GAME_COMPLETED_TYPE.toString() ||
				statusValue === GameCompletedEnum.RETIREMENT_TEAM_TWO_GAME_COMPLETED_TYPE.toString()
			) {
				// Determine the status text (FF for forfeit or RET for retirement)
				const statusText =
					statusValue === GameCompletedEnum.FORFEIT_TEAM_ONE_GAME_COMPLETED_TYPE.toString() ||
					statusValue === GameCompletedEnum.FORFEIT_TEAM_TWO_GAME_COMPLETED_TYPE.toString()
						? 'FF'
						: 'RET';

				// Apply status to the appropriate team
				// Status 2,3 is for team one (home)
				// Status 7,8 is for team two (away)
				const team =
					statusValue === GameCompletedEnum.FORFEIT_TEAM_ONE_GAME_COMPLETED_TYPE.toString() ||
					statusValue === GameCompletedEnum.RETIREMENT_TEAM_ONE_GAME_COMPLETED_TYPE.toString()
						? 'home'
						: 'away';

				updateMatchScores(match.matchUuid, team, {
					[`game${gameWord}Status`]: statusText
				});

				// break; // Exit loop after finding and applying the first valid status
			}
		}
	};

	const updateMatchFinalScores = (matchId: string, finalScores: { teamOneFinalScore: number; teamTwoFinalScore: number }) => {
		updateMatchScores(matchId, 'home', { final: finalScores.teamOneFinalScore.toString() });
		updateMatchScores(matchId, 'away', { final: finalScores.teamTwoFinalScore.toString() });
	};

	const getGameScores = (matchId: string, gameNumber: string) => {
		const gameNumberOnly = gameNumber.slice(-1);
		const matchScore = getMatchScores(matchId);
		const gameKey = `game${gameNumberOnly}` as keyof typeof matchScore.home;

		return {
			homeRetiredGameValue: tempMatchRetireScores.homeRetiredGameValue ?? matchScore.home[gameKey],
			awayRetiredGameValue: tempMatchRetireScores.awayRetiredGameValue ?? matchScore.away[gameKey]
		};
	};

	/**
	 * Saves retirement/forfeit scores and status for a game
	 * @param matchId - The match identifier
	 * @param gameNumber - The game number (e.g. 'G1')
	 * @param team - The team that retired/forfeited
	 * @param retireStatus - The retirement status ('RET' or 'FF')
	 */
	const saveRetireScores = (matchId: string, gameNumber: string, team: MatchSide, retireStatus: RetireOption) => {
		const { homeRetiredGameValue, awayRetiredGameValue } = getGameScores(matchId, gameNumber);

		const formattedGameNumber = `game${gameNumber.slice(1)}`.toLowerCase();
		const gameNumberWord = gameNumbers[parseInt(gameNumber.slice(1)) - 1];
		const formattedRetireStatus = {
			[`game${gameNumberWord}Status`]: retireStatus
		};
		const match = getMatch(matchId);
		const gameIndex = parseInt(gameNumber.slice(1));
		const maxScoreField = `scoreFormatGame${gameIndex}Max`;
		const maxScore = String(match?.[maxScoreField as keyof typeof match] ?? '11');

		if (retireStatus === 'FF') {
			if (team === 'home') {
				updateMatchScores(matchId, 'home', {
					[formattedGameNumber]: '0',
					...formattedRetireStatus
				});
				updateMatchScores(matchId, 'away', {
					[formattedGameNumber]: maxScore
				});
			} else {
				updateMatchScores(matchId, 'away', {
					[formattedGameNumber]: '0',
					...formattedRetireStatus
				});
				updateMatchScores(matchId, 'home', {
					[formattedGameNumber]: maxScore
				});
			}
			return;
		}

		if (retireStatus === 'RET') {
			if (team === 'home') {
				updateMatchScores(matchId, 'home', {
					[formattedGameNumber]: homeRetiredGameValue?.toString() || '0',
					...formattedRetireStatus
				});
			} else {
				updateMatchScores(matchId, 'away', {
					[formattedGameNumber]: awayRetiredGameValue?.toString() || '0',
					...formattedRetireStatus
				});
			}
			return;
		}

		// Handle UNDO cases
		if (retireStatus === 'UNDO_RET' || retireStatus === 'UNDO_FF') {
			if (team === 'home') {
				updateMatchScores(matchId, 'home', {
					...formattedRetireStatus
				});
			} else {
				updateMatchScores(matchId, 'away', {
					...formattedRetireStatus
				});
			}
		}

		setTempMatchRetireScores({ homeRetiredGameValue: undefined, awayRetiredGameValue: undefined });
	};

	const getRetiredGameNumber = (matchId: string): string[] => {
		const matchScore = getMatchScores(matchId);
		const statusProperties = ['gameOneStatus', 'gameTwoStatus', 'gameThreeStatus', 'gameFourStatus', 'gameFiveStatus'];
		const retiredGames: string[] = [];

		statusProperties.forEach((statusProp, index) => {
			const homeStatus = matchScore.home[statusProp as keyof typeof matchScore.home];
			const awayStatus = matchScore.away[statusProp as keyof typeof matchScore.away];

			if (homeStatus === 'RET' || homeStatus === 'FF' || awayStatus === 'RET' || awayStatus === 'FF') {
				retiredGames.push(`G${index + 1}`);
			}
		});

		return retiredGames;
	};

	const getRetiredTeam = (matchId: string): Record<string, MatchSide> => {
		const matchScore = getMatchScores(matchId);
		const statusProperties = ['gameOneStatus', 'gameTwoStatus', 'gameThreeStatus', 'gameFourStatus', 'gameFiveStatus'];
		const retiredTeams: Record<string, MatchSide> = {};

		statusProperties.forEach((statusProp, index) => {
			const homeStatus = matchScore.home[statusProp as keyof typeof matchScore.home];
			const awayStatus = matchScore.away[statusProp as keyof typeof matchScore.away];
			const gameNumber = `G${index + 1}`;

			if (homeStatus === 'RET' || homeStatus === 'FF') {
				retiredTeams[gameNumber] = 'home';
			} else if (awayStatus === 'RET' || awayStatus === 'FF') {
				retiredTeams[gameNumber] = 'away';
			}
		});

		return retiredTeams;
	};

	const getRetiredOption = (matchId: string): Record<string, RetireOption> => {
		const matchScore = getMatchScores(matchId);
		const statusFields = ['gameOneStatus', 'gameTwoStatus', 'gameThreeStatus', 'gameFourStatus', 'gameFiveStatus'];
		const retiredOptions: Record<string, RetireOption> = {};

		statusFields.forEach((status, index) => {
			const homeStatus = matchScore.home[status as keyof typeof matchScore.home];
			const awayStatus = matchScore.away[status as keyof typeof matchScore.away];
			const gameNumber = `G${index + 1}`;

			if (homeStatus === 'RET' || awayStatus === 'RET') {
				retiredOptions[gameNumber] = 'RET';
			} else if (homeStatus === 'FF' || awayStatus === 'FF') {
				retiredOptions[gameNumber] = 'FF';
			}
		});

		return retiredOptions;
	};

	const getMatch = (matchId: string) => {
		return matchupData.matches.find((match) => match.matchUuid === matchId);
	};

	// 0 - Draw, 1 - Home team winner, 2 - Away team winner
	const getMatchWinner = (matchId: string): 'home' | 'away' | 'draw' => {
		const matchScore = getMatchScores(matchId);
		const homeFinalScore = parseInt(matchScore.home.final);
		const awayFinalScore = parseInt(matchScore.away.final);

		if (homeFinalScore > awayFinalScore) {
			return 'home';
		} else if (awayFinalScore > homeFinalScore) {
			return 'away';
		}

		return 'draw';
	};

	/**
	 * Calculates adjusted scores for a retired/forfeited game
	 * @param matchId - The match identifier
	 * @param gameNumber - The game number (e.g. 'G1')
	 * @param games - Game configuration array
	 */
	const getAdjustedScores = (matchId: string, gameNumber: string, games: { max: number; winBy: number }[]): RetirementScores => {
		const retiredGames = getRetiredGameNumber(matchId);
		const retiredTeams = getRetiredTeam(matchId);
		const currentMatchScores = getMatchScores(matchId);

		if (!retiredGames.includes(gameNumber) || !retiredTeams[gameNumber]) {
			return { homeScore: 0, awayScore: 0 };
		}

		const retiredTeam = retiredTeams[gameNumber] as MatchSide;
		return calculateRetirementScores(gameNumber, games, currentMatchScores, retiredTeam);
	};

	/**
	 * Gets all adjusted scores for retired/forfeited games in a match
	 * @param matchId - The match identifier
	 * @param games - Game configuration array
	 */
	const getAllAdjustedScores = (matchId: string, games: { max: number; winBy: number }[]): AdjustedScores => {
		const retiredGames = getRetiredGameNumber(matchId);
		return retiredGames.reduce((acc, gameNumber) => {
			acc[gameNumber] = getAdjustedScores(matchId, gameNumber, games);
			return acc;
		}, {} as AdjustedScores);
	};

	return (
		<ScoreCardContext.Provider
			value={{
				lineups,
				updateMatchLineupStatus,
				getMatchLineupStatus,
				areAllLineupsSaved,
				updateSelectedPlayers,
				clearSelectedPlayers,
				getSelectedPlayers,
				haveSelectedPlayersChanged,
				updateMatchScores,
				getMatchScores,
				updateMatchFinalScores,
				matchupData,
				tempMatchRetireScores,
				setTempMatchRetireScores,
				saveRetireScores,
				getRetiredGameNumber,
				getRetiredTeam,
				getRetiredOption,
				searchTermPlayer,
				setSearchTermPlayer,
				checkAndSetRetiredStatus,
				getMatchWinner,
				getAdjustedScores,
				getAllAdjustedScores,
				getPlayerDoublesRating
			}}
		>
			{children}
		</ScoreCardContext.Provider>
	);
};

export const useScoreCard = () => {
	const context = useContext(ScoreCardContext);
	if (!context) {
		throw new Error('useScoreCard must be used within a ScoreCardProvider');
	}
	return context;
};
